import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { ShieldCheck, ShieldAlert, KeyRound } from "lucide-react";
import { useConfirm } from "./ConfirmDialog";

type Enroll = { id: string; qr: string; secret: string };

const SecurityManager = () => {
  const { toast } = useToast();
  const { confirm, dialog } = useConfirm();
  const [factorId, setFactorId] = useState<string | null>(null);
  const [enroll, setEnroll] = useState<Enroll | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "" });

  const load = async () => {
    const { data } = await supabase.auth.mfa.listFactors();
    setFactorId(data?.totp.find((f) => f.status === "verified")?.id ?? null);
  };
  useEffect(() => { load(); }, []);

  const start = async () => {
    setBusy(true);
    const { data: list } = await supabase.auth.mfa.listFactors();
    for (const f of list?.all ?? []) if (f.status !== "verified") await supabase.auth.mfa.unenroll({ factorId: f.id });
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: "totp", friendlyName: `App ${Date.now()}` });
    setBusy(false);
    if (error || !data) return toast({ title: "Não foi possível iniciar", description: error?.message, variant: "destructive" });
    setEnroll({ id: data.id, qr: data.totp.qr_code, secret: data.totp.secret });
  };

  const verify = async () => {
    if (!enroll) return;
    setBusy(true);
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId: enroll.id, code: code.trim() });
    setBusy(false);
    if (error) return toast({ title: "Código inválido", description: "Confira o código no aplicativo e tente de novo.", variant: "destructive" });
    setEnroll(null); setCode("");
    toast({ title: "2FA ativado", description: "A partir de agora o código será pedido em cada login." });
    load();
  };

  const disable = async () => {
    if (!factorId) return;
    const ok = await confirm({ title: "Desativar 2FA?", description: "Sua conta ficará protegida apenas pela senha e você não poderá gerenciar utilizadores.", confirmLabel: "Desativar", destructive: true });
    if (!ok) return;
    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    if (error) return toast({ title: "Erro", description: error.message, variant: "destructive" });
    toast({ title: "2FA desativado" });
    load();
  };

  const changePw = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pw.next.length < 10) return toast({ title: "Senha curta", description: "Use pelo menos 10 caracteres.", variant: "destructive" });
    const { error } = await supabase.auth.updateUser({ password: pw.next, current_password: pw.current } as never);
    if (error) return toast({ title: "Não foi possível trocar", description: error.message, variant: "destructive" });
    setPw({ current: "", next: "" });
    toast({ title: "Senha atualizada" });
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {dialog}
      <section className="rounded-xl border border-border bg-background p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {factorId ? <ShieldCheck className="h-5 w-5 text-primary" /> : <ShieldAlert className="h-5 w-5 text-destructive" />}
            <h2 className="font-heading font-semibold">Autenticação em dois fatores</h2>
          </div>
          <Badge variant={factorId ? "default" : "outline"}>{factorId ? "Ativo" : "Desativado"}</Badge>
        </div>
        <p className="text-sm text-muted-foreground">Além da senha, o login pede um código de 6 dígitos do aplicativo autenticador (Google Authenticator, Microsoft Authenticator, Authy ou 1Password).</p>

        {factorId && <Button variant="outline" onClick={disable}>Desativar 2FA</Button>}
        {!factorId && !enroll && <Button onClick={start} disabled={busy}>Ativar 2FA</Button>}
        {enroll && (
          <div className="space-y-4">
            <ol className="text-sm space-y-1 list-decimal pl-4">
              <li>Abra o aplicativo autenticador e escaneie o QR Code.</li>
              <li>Digite o código de 6 dígitos que aparecer.</li>
            </ol>
            <img src={enroll.qr} alt="QR Code do 2FA" className="h-44 w-44 rounded-lg border border-border bg-background p-2" />
            <p className="text-xs text-muted-foreground">Sem câmera? Digite a chave: <code className="font-mono break-all text-foreground">{enroll.secret}</code></p>
            <div className="flex gap-2">
              <Input inputMode="numeric" maxLength={6} placeholder="000000" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="max-w-[140px] tracking-widest text-center" />
              <Button onClick={verify} disabled={busy || code.length !== 6}>Confirmar</Button>
              <Button variant="ghost" onClick={() => { setEnroll(null); setCode(""); }}>Cancelar</Button>
            </div>
          </div>
        )}
      </section>

      <section className="rounded-xl border border-border bg-background p-6 space-y-4">
        <div className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-primary" /><h2 className="font-heading font-semibold">Trocar senha</h2></div>
        <form onSubmit={changePw} className="space-y-3">
          <Input type="password" placeholder="Senha atual" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} required autoComplete="current-password" />
          <Input type="password" placeholder="Nova senha (mín. 10 caracteres)" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} required autoComplete="new-password" />
          <Button type="submit">Salvar nova senha</Button>
        </form>
      </section>
    </div>
  );
};

export default SecurityManager;
